/**
 * Realtime device-handoff transport over the self-hosted Soketi (Pusher
 * protocol). This is a thin wrapper: it authorizes the private channel through
 * our own `pusher/auth` endpoint (so the session bearer never leaves our API)
 * and surfaces one signal — "the flow state changed" — to the caller.
 *
 * Deliberately, the caller RE-FETCHES the snapshot on that signal rather than
 * trusting the event payload: the broadcast is PII-free and minimal by design
 * (state/tags/available_events/done, no context), so the REST snapshot stays the
 * single source of truth.
 *
 * Connection params prefer the backend `RealtimeDescriptor` (from
 * `GET /collection-flow/realtime` or `collection/start`) and fall back to
 * `FRONTEND_PUSHER_*`. When the advertised host is localhost but the page is opened
 * on a LAN/tunnel hostname (phone after QR scan), the connection rides the PAGE's
 * OWN origin (host/port/TLS) — the dev server proxies `/app` → Soketi — because a
 * phone behind a Cloudflare tunnel can only reach the SPA origin (443), not
 * Soketi's own :6001. A loopback page (desktop) or an explicit reverse-proxied
 * `public_host` still dials Soketi directly.
 */
import Pusher from 'pusher-js';
import type { Channel } from 'pusher-js';

import { pusherAuth } from './client';
import type { RealtimeDescriptor } from './types';

/** The realtime event the backend broadcasts on every transition (engine-host persist). */
const STATE_CHANGE = 'state-change';
/** A reviewer wrote in the session chat (`case_messages`). Ids only; re-read the thread. */
const CHAT_MESSAGE = 'chat-message';
/** The in-page event the chat launcher listens for, so it shares this socket. */
export const CHAT_SIGNAL = 'trustsy:chat-message';

/**
 * The private channel for a flow. Mirrors the backend `realtime::flow_channel`
 * (Rust) — this cross-language convention must stay in lockstep with it. The
 * `collection/start` response also advertises this exact name.
 */
export function flowChannel(flowId: string): string {
  return `private-flow-${flowId}`;
}

export interface PusherConnection {
  key: string;
  host: string;
  port: number;
  tls: boolean;
}

/** Merge server descriptor + Vite env into a concrete connection (or null = off). */
export function resolvePusherConnection(
  descriptor?: RealtimeDescriptor | null,
  pageHostname: string = typeof window !== 'undefined' ? window.location.hostname : 'localhost',
  pageProtocol: string = typeof window !== 'undefined' ? window.location.protocol : 'http:',
  pagePort: string = typeof window !== 'undefined' ? window.location.port : '',
): PusherConnection | null {
  const envKey = import.meta.env.FRONTEND_PUSHER_KEY ?? '';
  const key = (descriptor?.key ?? envKey).trim();
  if (!key) return null;

  const envHost = import.meta.env.FRONTEND_PUSHER_HOST ?? 'localhost';
  const rawHost = (descriptor?.host ?? envHost).trim() || 'localhost';
  const host = rewriteLocalhostForPage(rawHost, pageHostname);

  // A loopback Soketi host rewritten to a real page host means the caller is a
  // phone on the LAN or behind a Cloudflare tunnel — it CANNOT reach Soketi's own
  // :6001 (the tunnel exposes only the SPA origin). Ride the page's OWN origin
  // instead: the Vite dev server proxies `/app` → Soketi, so the WS shares the
  // page host/port/TLS (wss on 443 through the tunnel, ws on :5273 on the LAN).
  if (host !== rawHost) {
    const tls = pageProtocol === 'https:';
    const port = pagePort ? Number(pagePort) : tls ? 443 : 80;
    return { key, host, port, tls };
  }

  // Direct: a loopback page (desktop → localhost:6001) or an explicitly-configured
  // reverse-proxied Soketi host (`public_host`) dials Soketi directly.
  const envPort = Number(import.meta.env.FRONTEND_PUSHER_PORT ?? '6001');
  const port = descriptor?.port ?? (Number.isFinite(envPort) ? envPort : 6001);

  const envTls = import.meta.env.FRONTEND_PUSHER_TLS === 'true';
  const tls =
    descriptor?.use_tls === true ||
    descriptor?.use_tls === false
      ? descriptor.use_tls
      : envTls || pageProtocol === 'https:';

  return { key, host, port, tls };
}

/**
 * Phone scanned a QR at `http://192.168.x.x:5273` but env/server say
 * `localhost` — dial Soketi on the page host so the phone can reach it.
 */
export function rewriteLocalhostForPage(configuredHost: string, pageHostname: string): string {
  const cfg = configuredHost.trim() || 'localhost';
  const page = pageHostname.trim();
  const cfgIsLoopback = cfg === 'localhost' || cfg === '127.0.0.1' || cfg === '::1';
  const pageIsLoopback =
    !page || page === 'localhost' || page === '127.0.0.1' || page === '::1';
  if (cfgIsLoopback && !pageIsLoopback) return page;
  return cfg;
}

/** True when a connection can be formed (server key or Vite key present). */
export function realtimeEnabled(descriptor?: RealtimeDescriptor | null): boolean {
  return resolvePusherConnection(descriptor) !== null;
}

export interface FlowSubscription {
  unsubscribe: () => void;
}

/**
 * Subscribe to the flow's private channel. `onStateChange` fires on every
 * broadcast; the caller should re-fetch the snapshot. Returns an inert handle
 * when realtime is disabled.
 */
export function subscribeFlow(opts: {
  token: string;
  /** Prefer the server channel; falls back to `private-flow-{id}` convention. */
  channel: string;
  descriptor?: RealtimeDescriptor | null;
  onStateChange: () => void;
  /** A new chat message exists; defaults to re-broadcasting {@link CHAT_SIGNAL}. */
  onChat?: () => void;
}): FlowSubscription {
  const conn = resolvePusherConnection(opts.descriptor);
  if (!conn) {
    return { unsubscribe: () => undefined };
  }

  const pusher = new Pusher(conn.key, {
    // `cluster` is required by the client type but ignored by Soketi.
    cluster: 'trustsy',
    wsHost: conn.host,
    wsPort: conn.port,
    wssPort: conn.port,
    forceTLS: conn.tls,
    enabledTransports: ['ws', 'wss'],
    channelAuthorization: {
      // customHandler bypasses the default endpoint entirely — we sign through
      // our applicant-plane authorizer with the session bearer.
      transport: 'ajax',
      endpoint: '',
      customHandler: ({ channelName, socketId }, callback) => {
        pusherAuth(opts.token, socketId, channelName)
          .then((auth) => callback(null, auth))
          .catch((err: unknown) => callback(err instanceof Error ? err : new Error('auth'), null));
      },
    },
  });

  const channel: Channel = pusher.subscribe(opts.channel);
  channel.bind(STATE_CHANGE, () => opts.onStateChange());
  channel.bind(CHAT_MESSAGE, () =>
    opts.onChat ? opts.onChat() : window.dispatchEvent(new Event(CHAT_SIGNAL)),
  );

  return {
    unsubscribe: () => {
      channel.unbind(STATE_CHANGE);
      channel.unbind(CHAT_MESSAGE);
      pusher.unsubscribe(opts.channel);
      pusher.disconnect();
    },
  };
}
